import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { AxiosError } from "axios";
import type { Socket } from "socket.io-client";
import AgentService from "@/services/ai/agent";
import { useToast } from "@/composables/ui/useToast";
import { AGENT_CONVERSATIONS_KEY } from "@/composables/agent/useAgentConversations";
import { AGENT_LINKEDIN_STATUS_KEY } from "@/composables/agent/useAgentLinkedInStatus";
import { AGENT_UNREAD_REPLY_DRAFTS_KEY } from "@/composables/agent/useReplyDraftInbox";
import { getSocket } from "@/utils/socket";
import type {
  AgentAction,
  AgentChatMessage,
  AgentChatMode,
  AgentChatRequest,
  AgentChatResponse,
  AgentCompanyResult,
  AgentContactResult,
  AgentConversationDetail,
  AgentModel,
  AgentPendingConfirmation,
  AgentProgressEvent,
  AgentReplyDraftContact,
  AgentStep,
  AgentStoredReplyDraft,
  AgentTurnEvent,
} from "@/dtos/Agent.dto";

/** An open AI reply draft thread: who it's about and its latest draft as it stands now. */
export interface AgentReplyThread {
  contact: AgentReplyDraftContact | null;
  draft: AgentAction | null;
}

const PROGRESS_EVENT = "agent:progress";
const TURN_EVENT = "agent:turn";
// A turn the server answered as still working: its result arrives over the socket, and the
// thread is polled in case that event is missed. The server ends a turn within 10 minutes.
const TURN_POLL_MS = 15_000;
const TURN_MAX_WAIT_MS = 11 * 60 * 1000;

// The rep's last model pick, remembered in this browser only.
const MODEL_STORAGE_KEY = "agent-chat-model";
// Gemini is switched off; a saved "gemini" pick falls back to Claude. Add it back here (and its
// option in AgentChatPanel.vue) to offer it again.
const AGENT_MODELS: AgentModel[] = ["claude" /* , "gemini" */];

const savedModel = (): AgentModel => {
  try {
    const value = localStorage.getItem(MODEL_STORAGE_KEY) as AgentModel | null;
    return value && AGENT_MODELS.includes(value) ? value : "claude";
  } catch {
    return "claude";
  }
};

// The mode a new conversation starts in: the rep's last pick, remembered in this browser only.
const MODE_STORAGE_KEY = "agent-chat-mode";

const savedMode = (): AgentChatMode => {
  try {
    return localStorage.getItem(MODE_STORAGE_KEY) === "assistant" ? "assistant" : "prospecting";
  } catch {
    return "prospecting";
  }
};

// The rep's timezone, so the assistant proposes meeting times in it.
const browserTimezone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

let messageSeq = 0;
const nextId = () => `agent-msg-${++messageSeq}`;

const errorText = (err: unknown): string => {
  const axiosErr = err as AxiosError<{ message?: string; msg?: string }>;
  const data = axiosErr?.response?.data;
  if (data?.message || data?.msg) return (data.message || data.msg) as string;
  // No response at all: the request timed out or never reached the server.
  if (axiosErr?.isAxiosError && !axiosErr.response) {
    return axiosErr.code === "ECONNABORTED" || axiosErr.code === "ETIMEDOUT"
      ? "That took too long and timed out. Please try again."
      : "Couldn't reach the server. Check your connection and try again.";
  }
  if (!axiosErr?.isAxiosError && err instanceof Error && err.message) return err.message;
  return "Something went wrong. Please try again.";
};

/**
 * Chat state for the in-app agent: the open thread's transcript, pending write
 * confirmation, send/confirm, and reopening a saved thread.
 *
 * `fixedMode` pins every conversation to one mode: the AI Agent page runs prospecting
 * only, and the floating assistant bubble runs the CRM assistant only.
 */
export function useAgentChat(options: { fixedMode?: AgentChatMode } = {}) {
  const { fixedMode } = options;
  const queryClient = useQueryClient();
  const toast = useToast();

  const conversationId = ref<string | null>(null);
  const messages = ref<AgentChatMessage[]>([]);
  const pending = ref<AgentPendingConfirmation | null>(null);
  // Set while the open thread is about a contact's reply and the AI's draft of an answer.
  const replyThread = ref<AgentReplyThread | null>(null);
  const isLoadingThread = ref(false);
  // The reopened thread has a search still running on the server.
  const isThreadWorking = ref(false);
  const model = ref<AgentModel>(savedModel());
  watch(model, (value) => {
    try {
      localStorage.setItem(MODEL_STORAGE_KEY, value);
    } catch {
      // Storage blocked: the pick still applies until the page reloads.
    }
  });
  // A conversation keeps the mode it started in; the picker only applies to a new one.
  const mode = ref<AgentChatMode>(fixedMode ?? savedMode());
  const isModeLocked = computed(
    () => !!fixedMode || !!conversationId.value || messages.value.length > 0
  );
  const pickMode = (value: AgentChatMode) => {
    if (isModeLocked.value) return;
    mode.value = value;
    try {
      localStorage.setItem(MODE_STORAGE_KEY, value);
    } catch {
      // Storage blocked: the pick still applies until the page reloads.
    }
  };
  // Bumped on every open/reset so a slow load can't overwrite a newer choice.
  let loadSeq = 0;

  // What the agent has reported doing in the turn that is running now.
  const liveSteps = ref<AgentStep[]>([]);
  let currentTurnId: string | null = null;

  const onProgress = (event: AgentProgressEvent) => {
    if (!event || event.turnId !== currentTurnId || !Array.isArray(event.steps)) return;
    liveSteps.value.push(...event.steps);
  };

  // Progress is a nicety: the chat still works if the socket can't be reached.
  // The socket is kept so unmounting detaches from it rather than opening a new one.
  let progressSocket: Socket | null = null;
  try {
    progressSocket = getSocket();
    progressSocket.on(PROGRESS_EVENT, onProgress);
  } catch (err) {
    console.error("Agent progress updates are unavailable:", err);
  }
  // Stops waiting on a still-working turn when the chat goes away.
  let stopWaiting: (() => void) | null = null;
  onBeforeUnmount(() => {
    progressSocket?.off(PROGRESS_EVENT, onProgress);
    stopWaiting?.();
    // Ends the wait on a reopened thread that is still running.
    loadSeq++;
  });

  /** Wait for a turn the server is still working on, and hand back its reply. */
  const waitForTurn = (started: AgentChatResponse): Promise<AgentChatResponse> =>
    new Promise((resolve, reject) => {
      const deadline = Date.now() + TURN_MAX_WAIT_MS;
      let timer: ReturnType<typeof setTimeout> | undefined;
      // Set once the wait ends, so a poll still in flight can't act after it.
      let settled = false;
      const stop = () => finish(() => reject(new Error("Stopped waiting for the search.")));
      const finish = (settle: () => void) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        progressSocket?.off(TURN_EVENT, onTurn);
        // Only clear our own handle; a newer turn may already be waiting.
        if (stopWaiting === stop) stopWaiting = null;
        settle();
      };
      // Without a turn id the event can't be told apart from other turns, so only polling counts.
      const onTurn = (event: AgentTurnEvent) => {
        if (!event || !started.turnId || event.turnId !== started.turnId) return;
        finish(() =>
          event.ok && event.data
            ? resolve(event.data)
            : reject(new Error(event.message || "Something went wrong. Please try again."))
        );
      };
      const poll = async () => {
        try {
          const result = await AgentService.getConversation(started.conversationId);
          if (settled) return;
          const thread = result.data.data as AgentConversationDetail;
          const last = thread.messages[thread.messages.length - 1];
          // Without the count from before the turn, an older reply could pass for this one,
          // so the thread must also have stopped working.
          const replied =
            started.savedMessages != null
              ? thread.messages.length > started.savedMessages
              : !thread.working;
          if (replied && last?.role === "assistant") {
            return finish(() =>
              resolve({
                conversationId: thread.id,
                reply: last.content,
                pendingConfirmation: thread.pendingConfirmation,
                companies: last.companies,
                contacts: last.contacts,
                steps: last.steps,
                actions: last.actions,
                replyDraft: last.replyDraft,
                replyDraftAction: thread.replyDraft,
              })
            );
          }
          if (!thread.working) {
            return finish(() =>
              reject(new Error("The search stopped before it finished. Please try again."))
            );
          }
        } catch (err) {
          if (settled) return;
          // A new thread whose turn failed is removed, so it is no longer found.
          if ((err as AxiosError)?.response?.status === 404) {
            return finish(() => reject(new Error("The search failed. Please try again.")));
          }
        }
        if (Date.now() > deadline) {
          return finish(() =>
            reject(
              new Error(
                "This search is taking longer than expected. Reopen it from your searches in a few minutes."
              )
            )
          );
        }
        timer = setTimeout(poll, TURN_POLL_MS);
      };
      progressSocket?.on(TURN_EVENT, onTurn);
      stopWaiting = stop;
      timer = setTimeout(poll, TURN_POLL_MS);
    });

  const push = (
    role: AgentChatMessage["role"],
    content: string,
    extras: {
      companies?: AgentCompanyResult[];
      contacts?: AgentContactResult[];
      steps?: AgentStep[];
      actions?: AgentAction[];
      replyDraft?: AgentStoredReplyDraft;
    } = {}
  ) => {
    if (!content.trim()) return;
    const { companies, contacts, steps, actions, replyDraft } = extras;
    messages.value.push({
      id: nextId(),
      role,
      content,
      ...(companies?.length ? { companies } : {}),
      ...(contacts?.length ? { contacts } : {}),
      ...(steps?.length ? { steps } : {}),
      ...(actions?.length ? { actions } : {}),
      ...(replyDraft ? { replyDraft } : {}),
    });
  };

  /** Put an action's latest state on the reply that proposed it. */
  const updateAction = (action: AgentAction) => {
    const thread = replyThread.value;
    if (thread?.draft?.id === action.id) {
      replyThread.value = {
        ...thread,
        draft: { ...action, contact: action.contact ?? thread.draft.contact },
      };
      return;
    }
    for (const message of messages.value) {
      const index = message.actions?.findIndex((a) => a.id === action.id) ?? -1;
      if (index === -1) continue;
      // Keeps the contact the server attached when the update came without one.
      const previous = message.actions![index];
      message.actions![index] = { ...action, contact: action.contact ?? previous.contact };
      return;
    }
  };

  const mutation = useMutation({
    mutationFn: async (body: AgentChatRequest): Promise<AgentChatResponse> => {
      // randomUUID needs a secure context; without it the turn runs with no live progress.
      currentTurnId = typeof crypto?.randomUUID === "function" ? crypto.randomUUID() : null;
      liveSteps.value = [];
      const result = await AgentService.chat({
        ...body,
        ...(currentTurnId ? { turnId: currentTurnId } : {}),
      });
      if (!result.data.status) throw new Error(result.data.msg);
      const data = result.data.data as AgentChatResponse;
      if (!data.working) return data;
      // The thread exists now even if the turn is its first, so later actions can name it.
      conversationId.value = data.conversationId;
      return waitForTurn(data);
    },
    // Which chat the turn belongs to, so its reply can't land in one opened after a reset.
    onMutate: () => ({ seq: loadSeq }),
    onSuccess: (data, _body, context) => {
      if (context?.seq !== loadSeq) return;
      conversationId.value = data.conversationId;
      // The server's list is the full record; the live one covers a dropped response field.
      push("assistant", data.reply, {
        companies: data.companies,
        contacts: data.contacts,
        steps: data.steps?.length ? data.steps : liveSteps.value,
        actions: data.actions,
        replyDraft: data.replyDraft,
      });
      // The assistant may have rewritten the thread's draft.
      if (replyThread.value && data.replyDraftAction !== undefined) {
        replyThread.value = { ...replyThread.value, draft: data.replyDraftAction };
      }
      pending.value = data.pendingConfirmation;
      // A new thread appeared, or this one moved to the top of the list.
      queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
    },
    onError: (err, _body, context) => {
      if (context?.seq !== loadSeq) return;
      push("error", errorText(err));
    },
    onSettled: () => {
      currentTurnId = null;
      liveSteps.value = [];
      // A search can fail on a LinkedIn account that dropped since the status was loaded.
      queryClient.invalidateQueries({ queryKey: AGENT_LINKEDIN_STATUS_KEY });
    },
  });

  /** Send a message, a CSV/XLS/XLSX of companies or people, or both. */
  const send = (text: string, file?: File) => {
    const message = text.trim();
    if ((!message && !file) || mutation.isPending.value || isLoadingThread.value) return;
    push(
      "user",
      file
        ? `${message || "Find prospects from the attached file."}\n\n(Attached ${file.name})`
        : message
    );
    // A new message while an action is pending declines it server-side.
    pending.value = null;
    const assistant = mode.value === "assistant";
    mutation.mutate({
      conversationId: conversationId.value ?? undefined,
      ...(assistant ? { mode: "assistant", timezone: browserTimezone() } : { model: model.value }),
      ...(message ? { message } : {}),
      // The assistant takes text only; the composer hides the attach button for it.
      ...(file && !assistant ? { file } : {}),
    });
  };

  /** Look up people at the companies the rep ticked (its own workflow, not a chat message). */
  const findContacts = (companies: AgentCompanyResult[]) => {
    if (companies.length === 0 || !conversationId.value) return;
    if (mutation.isPending.value || isLoadingThread.value) return;
    push("user", `Find contacts at: ${companies.map((c) => c.name).join(", ")}`);
    pending.value = null;
    mutation.mutate({
      conversationId: conversationId.value,
      findContacts: {
        companies: companies.map((c) => ({ name: c.name, linkedinUrl: c.linkedinUrl })),
      },
    });
  };

  const respond = (approved: boolean) => {
    if (!pending.value || !conversationId.value || mutation.isPending.value) return;
    const confirmation = pending.value;
    const threadId = conversationId.value;
    const { token, actions } = confirmation;
    push("user", approved ? `Confirmed: ${actions.map((a) => a.title).join("; ")}` : "Cancelled.");
    pending.value = null;
    mutation.mutate(
      { conversationId: threadId, confirmation: { token, approved } },
      {
        // The answer never landed, so the card comes back for another try.
        onError: () => {
          if (conversationId.value === threadId) pending.value = confirmation;
        },
      }
    );
  };

  /**
   * Reopen a saved thread. Ignored mid-turn: the reply would land in the wrong thread.
   * `reload` reads the open thread again, e.g. when a new reply draft was posted to it.
   * `markRead: false` loads a reply thread without counting it as seen (the window is shut).
   */
  const open = async (id: string, options: { reload?: boolean; markRead?: boolean } = {}) => {
    if (mutation.isPending.value) return;
    if (id === conversationId.value && !options.reload) {
      // Already waiting on this thread's running search: keep waiting.
      if (isThreadWorking.value) return;
      // Picking the open thread again calls off a load of another one.
      loadSeq++;
      isLoadingThread.value = false;
      return;
    }
    const seq = ++loadSeq;
    isLoadingThread.value = true;
    isThreadWorking.value = false;
    const load = async (): Promise<AgentConversationDetail | null> => {
      const result = await AgentService.getConversation(id);
      if (seq !== loadSeq) return null;
      if (!result.data.status) throw new Error(result.data.msg);
      const thread = result.data.data as AgentConversationDetail;
      conversationId.value = thread.id;
      messages.value = thread.messages.map((m) => ({
        id: nextId(),
        role: m.role,
        content: m.content,
        ...(m.companies?.length ? { companies: m.companies } : {}),
        ...(m.contacts?.length ? { contacts: m.contacts } : {}),
        ...(m.steps?.length ? { steps: m.steps } : {}),
        ...(m.actions?.length ? { actions: m.actions } : {}),
        ...(m.replyDraft ? { replyDraft: m.replyDraft } : {}),
      }));
      mode.value = fixedMode ?? thread.mode ?? "prospecting";
      pending.value = thread.pendingConfirmation;
      replyThread.value =
        thread.kind === "reply_draft"
          ? { contact: thread.contact ?? null, draft: thread.replyDraft ?? null }
          : null;
      return thread;
    };
    // Opening a reply thread reads its draft: it drops off the bubble and loses its dot.
    const markRead = (thread: AgentConversationDetail | null) => {
      if (thread?.kind !== "reply_draft" || options.markRead === false) return;
      AgentService.markConversationRead(thread.id)
        .then(() => {
          queryClient.invalidateQueries({ queryKey: AGENT_UNREAD_REPLY_DRAFTS_KEY });
          queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
        })
        .catch((err) => console.error("Couldn't mark the conversation read:", err));
    };
    try {
      let thread = await load();
      markRead(thread);
      // A search still running on the server keeps the thread busy, so nothing is sent
      // over it, and is reloaded until it finishes. Opening another thread ends the wait.
      const deadline = Date.now() + TURN_MAX_WAIT_MS;
      while (thread?.working && Date.now() < deadline) {
        isThreadWorking.value = true;
        await new Promise((resolve) => setTimeout(resolve, TURN_POLL_MS));
        if (seq !== loadSeq) return;
        thread = await load();
        if (thread && !thread.working) {
          queryClient.invalidateQueries({ queryKey: AGENT_CONVERSATIONS_KEY });
        }
      }
    } catch (err) {
      if (seq === loadSeq) toast.error(errorText(err));
    } finally {
      if (seq === loadSeq) {
        isLoadingThread.value = false;
        isThreadWorking.value = false;
      }
    }
  };

  const reset = () => {
    stopWaiting?.();
    loadSeq++;
    isThreadWorking.value = false;
    isLoadingThread.value = false;
    conversationId.value = null;
    messages.value = [];
    pending.value = null;
    replyThread.value = null;
    // An opened thread set its own mode; a new chat starts in the rep's last pick.
    mode.value = fixedMode ?? savedMode();
  };

  return {
    conversationId,
    messages,
    pending,
    replyThread,
    liveSteps,
    model,
    mode,
    isModeLocked,
    pickMode,
    updateAction,
    isSending: mutation.isPending,
    isLoadingThread,
    isThreadWorking,
    send,
    findContacts,
    respond,
    open,
    reset,
  };
}
